import type { Clip } from '../types'

/**
 * Dò điểm lấy nét (focus) tự động dựa trên khuôn mặt, dùng FaceDetector API
 * gốc của trình duyệt (nếu có). Lấy mẫu vài khung hình rồi trung bình vị trí mặt.
 * Trả về null nếu trình duyệt không hỗ trợ hoặc không thấy khuôn mặt
 * → gọi bên ngoài sẽ fallback về crop giữa.
 */
export async function detectFaceFocus(clip: Clip): Promise<{ fx: number; fy: number } | null> {
  const FD = (window as unknown as { FaceDetector?: new (o?: unknown) => FaceDetectorLike })
    .FaceDetector
  if (!FD) return null

  const detector = new FD({ fastMode: true, maxDetectedFaces: 5 })
  const video = document.createElement('video')
  video.src = clip.url
  video.muted = true
  video.playsInline = true

  try {
    await new Promise<void>((res, rej) => {
      video.onloadeddata = () => res()
      video.onerror = () => rej(new Error('no video'))
    })
    const canvas = document.createElement('canvas')
    canvas.width = clip.width || video.videoWidth
    canvas.height = clip.height || video.videoHeight
    const ctx = canvas.getContext('2d')!

    const samples = [0.15, 0.35, 0.55, 0.75].map((f) => f * (clip.duration || 1))
    const centers: { x: number; y: number }[] = []

    for (const t of samples) {
      await seek(video, t)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      try {
        const faces = await detector.detect(canvas)
        for (const f of faces) {
          const b = f.boundingBox
          centers.push({
            x: (b.x + b.width / 2) / canvas.width,
            y: (b.y + b.height / 2) / canvas.height,
          })
        }
      } catch {
        /* bỏ qua khung lỗi */
      }
    }

    if (centers.length === 0) return null
    const fx = centers.reduce((a, c) => a + c.x, 0) / centers.length
    const fy = centers.reduce((a, c) => a + c.y, 0) / centers.length
    return { fx, fy }
  } catch {
    return null
  }
}

function seek(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      resolve()
    }
    video.addEventListener('seeked', onSeeked)
    video.currentTime = t
  })
}

// Kiểu tối giản cho FaceDetector API (chưa có sẵn trong lib.dom.d.ts).
interface FaceDetectorLike {
  detect(source: CanvasImageSource): Promise<{ boundingBox: DOMRectReadOnly }[]>
}
